import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { aboutContent, companyInfo, stats } from "../data/mockData";
import {
  Target,
  Eye,
  Award,
  Users,
  Globe,
  ArrowRight,
  Mail,
  Phone,
  CheckCircle,
  GraduationCap,
  Building2,
  Shield,
} from "lucide-react";


const FadeIn = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const AboutPage = () => {
  return (
    <div>
      {/* Page Banner */}
      <section className="relative h-[40vh] min-h-[300px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=1920&q=80)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/70" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 w-full">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-0.5 bg-orange-500" />
                <span className="text-orange-400 text-sm font-medium tracking-wider uppercase">
                  Know More
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                About <span className="text-orange-400">Us</span>
              </h1>
              <p className="text-slate-300 mt-3 max-w-lg">
                Learn about our journey, mission, and the team behind your
                overseas education dreams.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Floating 3D elements */}
        <motion.div
          animate={{ y: [0, -12, 0], rotate: [0, 5, -5, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-20 right-20 z-10 hidden lg:block"
        >
          <div className="w-14 h-14 bg-orange-500/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-orange-500/30">
            <GraduationCap size={24} className="text-orange-400" />
          </div>
        </motion.div>
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute bottom-20 right-40 z-10 hidden lg:block"
        >
          <div className="w-12 h-12 bg-blue-500/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-blue-500/30">
            <Globe size={20} className="text-blue-400" />
          </div>
        </motion.div>
      </section>

      {/* About Content */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1544717305-2782549b5136?w=800&q=80"
                    alt="Education consultancy"
                    className="w-full h-[450px] object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 hidden md:block">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                      <Award size={22} className="text-orange-500" />
                    </div>
                    <div>
                      <div className="text-xl font-bold text-slate-800">Since 2010</div>
                      <div className="text-sm text-slate-500">Trusted Consultancy</div>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-orange-100 rounded-2xl -z-10" />
              </div>
            </FadeIn>

            <FadeIn delay={0.2}>
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-0.5 bg-orange-500" />
                  <span className="text-orange-500 text-sm font-semibold tracking-wider uppercase">
                    Our Story
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                  Welcome to{" "}
                  <span className="text-orange-500">Sun Lumia Overseas</span>
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  {aboutContent.story}
                </p>
                <p className="text-slate-600 leading-relaxed mb-6">
                  {aboutContent.achievement}
                </p>
                <div className="space-y-3">
                  {[
                    "Free counselling for all students",
                    "150+ partner universities globally",
                    "97% visa success rate",
                    "Complete documentation support",
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle size={18} className="text-orange-500 flex-shrink-0" />
                      <span className="text-slate-700 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-slate-50 px-4">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className="w-10 h-0.5 bg-orange-500" />
                <span className="text-orange-500 text-sm font-semibold tracking-wider uppercase">
                  What Drives Us
                </span>
                <div className="w-10 h-0.5 bg-orange-500" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800">
                Our Mission &{" "}
                <span className="text-orange-500">Vision</span>
              </h2>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8">
            <FadeIn delay={0.1}>
              <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow h-full">
                <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center mb-5">
                  <Target size={26} className="text-orange-500" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-3">Our Mission</h3>
                <p className="text-slate-600 leading-relaxed">
                  {aboutContent.mission}
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.2}>
              <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow h-full">
                <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center mb-5">
                  <Eye size={26} className="text-orange-500" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-3">Our Vision</h3>
                <p className="text-slate-600 leading-relaxed">
                  {aboutContent.vision}
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className="w-10 h-0.5 bg-orange-500" />
                <span className="text-orange-500 text-sm font-semibold tracking-wider uppercase">
                  Leadership
                </span>
                <div className="w-10 h-0.5 bg-orange-500" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800">
                Meet Our{" "}
                <span className="text-orange-500">Founder</span>
              </h2>
            </div>
          </FadeIn>

          <FadeIn delay={0.15}>
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="grid md:grid-cols-5 gap-0">
                  {/* Photo */}
                  <div className="md:col-span-2 relative overflow-hidden">
                    <img
                      src={aboutContent.founderImage}
                      alt={aboutContent.founderName}
                      className="w-full h-full min-h-[350px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent md:bg-gradient-to-r md:from-transparent md:to-transparent" />
                  </div>
                  {/* Info */}
                  <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center">
                    <div className="inline-flex items-center gap-2 bg-orange-50 text-orange-600 px-3 py-1.5 rounded-lg text-xs font-semibold mb-4 self-start">
                      <Shield size={14} />
                      Founder & Director
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-2">
                      {aboutContent.founderName}
                    </h3>
                    <p className="text-slate-600 leading-relaxed mb-6">
                      {aboutContent.founderBio}
                    </p>
                    <div className="space-y-3">
                      <a
                        href={`mailto:${companyInfo.email}`}
                        className="flex items-center gap-3 text-slate-600 hover:text-orange-500 transition-colors text-sm"
                      >
                        <Mail size={16} className="text-orange-500" />
                        {companyInfo.email}
                      </a>
                      <a
                        href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}
                        className="flex items-center gap-3 text-slate-600 hover:text-orange-500 transition-colors text-sm"
                      >
                        <Phone size={16} className="text-orange-500" />
                        {companyInfo.phone}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-slate-900 px-4">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className="w-10 h-0.5 bg-orange-500" />
                <span className="text-orange-400 text-sm font-semibold tracking-wider uppercase">
                  Why Us
                </span>
                <div className="w-10 h-0.5 bg-orange-500" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Why Choose{" "}
                <span className="text-orange-400">Sun Lumia?</span>
              </h2>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: GraduationCap,
                title: "Expert Counsellors",
                desc: "Highly experienced team with deep knowledge of international education systems.",
              },
              {
                icon: Globe,
                title: "Global Network",
                desc: "Partnerships with 150+ universities across 8+ countries worldwide.",
              },
              {
                icon: Shield,
                title: "97% Success Rate",
                desc: "Proven track record of visa approvals with thorough documentation support.",
              },
              {
                icon: Building2,
                title: "Since 2010",
                desc: "Over 15 years of trusted service in the overseas education industry.",
              },
            ].map((item, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div className="bg-slate-800 rounded-2xl p-6 text-center hover:bg-slate-800/80 transition-all duration-300 hover:-translate-y-1 group">
                  <div className="w-14 h-14 bg-orange-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-500/20 transition-colors">
                    <item.icon size={26} className="text-orange-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {item.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Start Your Study Abroad Journey Today
            </h2>
            <p className="text-orange-100 text-lg mb-8 max-w-2xl mx-auto">
              Contact us for a free consultation. Our expert counsellors are
              ready to guide you every step of the way.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-orange-600 rounded-lg font-semibold hover:bg-orange-50 transition-all duration-300 hover:shadow-lg"
            >
              Contact Us
              <ArrowRight size={16} />
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
